// 視聴側レイアウトプリセット管理 (Phase 4)。
// 仕様書: SPECIFICATION.md 6.2.3-6.2.5
//
// プリセット:
//   1 ウィンドウ: fullscreen (デフォルト) / free
//   2 ウィンドウ: horizontal / vertical / pip / free
//   3 ウィンドウ: main-right / main-bottom / columns / rows / free
//
// 永続化: LocalStorage キー WinCast-viewer-settings に ViewerSettings を保存。
// 次回接続時、同じウィンドウ数なら前回プリセットを自動適用。
// "free" 選択時の各 video の位置・サイズはウィンドウ数ごとに別途保存。

import type {
  FreeLayoutItem,
  OneWindowLayout,
  ThreeWindowLayout,
  TwoWindowLayout,
  ViewerSettings,
} from "@wincast/shared";
import { VIEWER_SETTINGS_STORAGE_KEY } from "@wincast/shared";
import { t } from "./i18n.js";

export type LayoutKey = OneWindowLayout | TwoWindowLayout | ThreeWindowLayout;

const ONE_LAYOUTS: OneWindowLayout[] = ["fullscreen", "free", "room"];
const TWO_LAYOUTS: TwoWindowLayout[] = [
  "horizontal",
  "vertical",
  "pip",
  "free",
  "room",
];
const THREE_LAYOUTS: ThreeWindowLayout[] = [
  "main-right",
  "main-bottom",
  "columns",
  "rows",
  "free",
  "room",
];

// ラベルは i18n の翻訳キーを保持。getLayoutOptions で t() を呼んで実文字列を解決する。
const ONE_LABEL_KEYS: Record<OneWindowLayout, "layout.fullscreen" | "layout.free" | "layout.room"> = {
  fullscreen: "layout.fullscreen",
  free: "layout.free",
  room: "layout.room",
};

const TWO_LABEL_KEYS: Record<TwoWindowLayout, "layout.horizontal" | "layout.vertical" | "layout.pip" | "layout.free" | "layout.room"> = {
  horizontal: "layout.horizontal",
  vertical: "layout.vertical",
  pip: "layout.pip",
  free: "layout.free",
  room: "layout.room",
};

const THREE_LABEL_KEYS: Record<ThreeWindowLayout, "layout.mainRight" | "layout.mainBottom" | "layout.columns" | "layout.rows" | "layout.free" | "layout.room"> = {
  "main-right": "layout.mainRight",
  "main-bottom": "layout.mainBottom",
  columns: "layout.columns",
  rows: "layout.rows",
  free: "layout.free",
  room: "layout.room",
};

const DEFAULT_ONE: OneWindowLayout = "fullscreen";
const DEFAULT_TWO: TwoWindowLayout = "horizontal";
// SPECIFICATION.md 8.1 は 3 面の既定を明示していないため、3 枚を均等に並べて
// 一覧性が高い "columns" を既定とする。
const DEFAULT_THREE: ThreeWindowLayout = "columns";

export function loadSettings(): ViewerSettings {
  try {
    const raw = localStorage.getItem(VIEWER_SETTINGS_STORAGE_KEY);
    if (!raw) return { preferredLayout: {} };
    const parsed = JSON.parse(raw) as unknown;
    if (typeof parsed !== "object" || parsed === null) {
      return { preferredLayout: {} };
    }
    const prefs = (parsed as { preferredLayout?: unknown }).preferredLayout;
    const free = (parsed as { freeLayout?: unknown }).freeLayout;
    const validPrefs = parseValidPrefs(prefs);
    const validFree = parseValidFreeLayout(free);
    return {
      preferredLayout: validPrefs,
      ...(validFree ? { freeLayout: validFree } : {}),
    };
  } catch {
    return { preferredLayout: {} };
  }
}

function parseValidPrefs(prefs: unknown): ViewerSettings["preferredLayout"] {
  if (typeof prefs !== "object" || prefs === null) return {};
  const p = prefs as {
    oneWindow?: unknown;
    twoWindows?: unknown;
    threeWindows?: unknown;
  };
  const one = ONE_LAYOUTS.includes(p.oneWindow as OneWindowLayout)
    ? (p.oneWindow as OneWindowLayout)
    : undefined;
  const two = TWO_LAYOUTS.includes(p.twoWindows as TwoWindowLayout)
    ? (p.twoWindows as TwoWindowLayout)
    : undefined;
  const three = THREE_LAYOUTS.includes(p.threeWindows as ThreeWindowLayout)
    ? (p.threeWindows as ThreeWindowLayout)
    : undefined;
  return {
    ...(one !== undefined ? { oneWindow: one } : {}),
    ...(two !== undefined ? { twoWindows: two } : {}),
    ...(three !== undefined ? { threeWindows: three } : {}),
  };
}

function parseValidFreeLayout(
  free: unknown,
): ViewerSettings["freeLayout"] | undefined {
  if (typeof free !== "object" || free === null) return undefined;
  const f = free as Record<string, unknown>;
  const out: ViewerSettings["freeLayout"] = {};
  for (const key of ["1", "2", "3"] as const) {
    const items = f[key];
    if (!Array.isArray(items)) continue;
    const validated = items.filter(isFreeLayoutItem);
    if (validated.length > 0) out[key] = validated;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

function isFreeLayoutItem(v: unknown): v is FreeLayoutItem {
  if (typeof v !== "object" || v === null) return false;
  const o = v as Record<string, unknown>;
  return (
    (o.index === 1 || o.index === 2 || o.index === 3) &&
    typeof o.x === "number" &&
    typeof o.y === "number" &&
    typeof o.width === "number" &&
    typeof o.height === "number" &&
    typeof o.z === "number"
  );
}

export function saveSettings(settings: ViewerSettings): void {
  try {
    localStorage.setItem(
      VIEWER_SETTINGS_STORAGE_KEY,
      JSON.stringify(settings),
    );
  } catch {
    // LocalStorage 利用不可 (シークレットモード等) は無視
  }
}

export function getLayoutForCount(
  count: number,
  settings: ViewerSettings,
): LayoutKey | null {
  if (count === 1) return settings.preferredLayout.oneWindow ?? DEFAULT_ONE;
  if (count === 2) return settings.preferredLayout.twoWindows ?? DEFAULT_TWO;
  if (count === 3) return settings.preferredLayout.threeWindows ?? DEFAULT_THREE;
  return null;
}

export type LayoutOption = { value: LayoutKey; label: string };

export function getLayoutOptions(count: number): LayoutOption[] {
  if (count === 1) {
    return ONE_LAYOUTS.map((k) => ({ value: k, label: t(ONE_LABEL_KEYS[k]) }));
  }
  if (count === 2) {
    return TWO_LAYOUTS.map((k) => ({ value: k, label: t(TWO_LABEL_KEYS[k]) }));
  }
  if (count === 3) {
    return THREE_LAYOUTS.map((k) => ({ value: k, label: t(THREE_LABEL_KEYS[k]) }));
  }
  return [];
}

// 各レイアウトのミニ図形 SVG。24×24 viewBox、stroke=currentColor で色は CSS 側に委譲。
// 視覚的に "どのレイアウトか" が一目で分かることを優先し、テキストは置かない。
export function getLayoutIconSvg(value: LayoutKey): string {
  switch (value) {
    case "fullscreen":
      return svg`<rect x="3" y="5" width="18" height="14" rx="1.5"/>`;
    case "horizontal":
      return svg`<rect x="3" y="5" width="8.5" height="14" rx="1.2"/><rect x="12.5" y="5" width="8.5" height="14" rx="1.2"/>`;
    case "vertical":
      return svg`<rect x="3" y="5" width="18" height="6.5" rx="1.2"/><rect x="3" y="12.5" width="18" height="6.5" rx="1.2"/>`;
    case "pip":
      return svg`<rect x="3" y="5" width="18" height="14" rx="1.5"/><rect x="13" y="12.5" width="6.5" height="5" rx="1" fill="currentColor" fill-opacity="0.85" stroke="none"/>`;
    case "main-right":
      return svg`<rect x="3" y="5" width="11" height="14" rx="1.2"/><rect x="15" y="5" width="6" height="6.5" rx="1"/><rect x="15" y="12.5" width="6" height="6.5" rx="1"/>`;
    case "main-bottom":
      return svg`<rect x="3" y="5" width="18" height="8" rx="1.2"/><rect x="3" y="14" width="8.5" height="5" rx="1"/><rect x="12.5" y="14" width="8.5" height="5" rx="1"/>`;
    case "columns":
      return svg`<rect x="3" y="5" width="5" height="14" rx="1"/><rect x="9.5" y="5" width="5" height="14" rx="1"/><rect x="16" y="5" width="5" height="14" rx="1"/>`;
    case "rows":
      return svg`<rect x="3" y="5" width="18" height="4" rx="0.8"/><rect x="3" y="10" width="18" height="4" rx="0.8"/><rect x="3" y="15" width="18" height="4" rx="0.8"/>`;
    case "free":
      // 重なった矩形 3 つで自由配置を示唆
      return svg`<rect x="3" y="5" width="10" height="8" rx="1.2"/><rect x="11" y="9" width="10" height="8" rx="1.2"/><rect x="6" y="13" width="9" height="6" rx="1.2"/>`;
    case "room":
      // 1 点透視の 3 壁: 中央に正面、左右に奥行を示す台形
      return svg`<polygon points="3,5 8,7 8,17 3,19"/><polygon points="21,5 16,7 16,17 21,19"/><rect x="8" y="7" width="8" height="10" rx="0.6"/>`;
    default:
      return svg`<rect x="3" y="5" width="18" height="14" rx="1.5"/>`;
  }
}

// SVG ラッパ。stroke は currentColor、stroke-width はデザイン上の太さに揃える。
function svg(strings: TemplateStringsArray, ...values: unknown[]): string {
  const inner = strings.reduce(
    (acc, s, i) => acc + s + (i < values.length ? String(values[i] ?? "") : ""),
    "",
  );
  return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round">${inner}</svg>`;
}

export function persistPreferredLayout(
  count: number,
  layout: LayoutKey,
  settings: ViewerSettings,
): ViewerSettings {
  const next: ViewerSettings = {
    preferredLayout: { ...settings.preferredLayout },
    ...(settings.freeLayout ? { freeLayout: settings.freeLayout } : {}),
  };
  if (count === 1 && ONE_LAYOUTS.includes(layout as OneWindowLayout)) {
    next.preferredLayout.oneWindow = layout as OneWindowLayout;
  } else if (count === 2 && TWO_LAYOUTS.includes(layout as TwoWindowLayout)) {
    next.preferredLayout.twoWindows = layout as TwoWindowLayout;
  } else if (
    count === 3 &&
    THREE_LAYOUTS.includes(layout as ThreeWindowLayout)
  ) {
    next.preferredLayout.threeWindows = layout as ThreeWindowLayout;
  }
  saveSettings(next);
  return next;
}

export function getFreeLayout(
  count: number,
  settings: ViewerSettings,
): FreeLayoutItem[] | null {
  if (count !== 1 && count !== 2 && count !== 3) return null;
  return settings.freeLayout?.[String(count) as "1" | "2" | "3"] ?? null;
}

export function persistFreeLayout(
  count: number,
  items: FreeLayoutItem[],
  settings: ViewerSettings,
): ViewerSettings {
  if (count !== 1 && count !== 2 && count !== 3) return settings;
  const key = String(count) as "1" | "2" | "3";
  const next: ViewerSettings = {
    preferredLayout: { ...settings.preferredLayout },
    freeLayout: { ...(settings.freeLayout ?? {}), [key]: items },
  };
  saveSettings(next);
  return next;
}

// デフォルト自由配置を生成。
// 各 video のアスペクト比 (aspects[idx]) とステージのアスペクト比 (stageAspect)
// から、各アイテムが letterbox 無しで動画と同じ縦横比になるよう % サイズを算出する。
// 1 ウィンドウ: ステージにフィットする最大サイズで中央配置。
// 複数ウィンドウ: 高さを揃えて横一列に並べ、必要なら全体をスケールダウン。
export function makeDefaultFreeLayout(
  indices: (1 | 2 | 3)[],
  aspects: Map<1 | 2 | 3, number>,
  stageAspect: number,
): FreeLayoutItem[] {
  const n = indices.length;
  if (n === 0) return [];
  const safeStage = stageAspect > 0 ? stageAspect : 16 / 9;
  const fallback = 16 / 9;

  if (n === 1) {
    const idx = indices[0];
    if (idx === undefined) return [];
    const va = aspects.get(idx) ?? fallback;
    // video が stage より横長 → 横幅 100% を使い高さで合わせる
    // 縦長 → 高さ 100% を使い幅で合わせる
    let w = 100;
    let h = 100;
    if (va > safeStage) {
      h = (100 * safeStage) / va;
    } else {
      w = (100 * va) / safeStage;
    }
    return [
      {
        index: idx,
        x: (100 - w) / 2,
        y: (100 - h) / 2,
        width: w,
        height: h,
        z: 1,
      },
    ];
  }

  // n >= 2: 横一列に並べる。target 高さは 60% から始め、総幅 (+gap) が 96% を
  // 超えたら全体を縮小して 96% に収める。
  const gap = 2;
  let targetH = 60;
  const computeWidths = (h: number): number[] =>
    indices.map((idx) => {
      const va = aspects.get(idx) ?? fallback;
      // 画素換算: W_px / H_px = va, W_px = W% * stageW, H_px = h% * stageH
      // ⇒ (W% * stageW) / (h% * stageH) = va
      // ⇒ W% = (h% * va * stageH) / stageW = (h% * va) / safeStage
      return (h * va) / safeStage;
    });

  let widths = computeWidths(targetH);
  let totalW = widths.reduce((a, b) => a + b, 0) + gap * (n - 1);
  if (totalW > 96) {
    const scale = 96 / totalW;
    targetH *= scale;
    widths = widths.map((w) => w * scale);
    totalW = widths.reduce((a, b) => a + b, 0) + gap * (n - 1);
  }

  const startX = (100 - totalW) / 2;
  const y = (100 - targetH) / 2;

  let x = startX;
  return indices.map((idx, i) => {
    const w = widths[i] ?? 0;
    const item: FreeLayoutItem = {
      index: idx,
      x,
      y,
      width: w,
      height: targetH,
      z: i + 1,
    };
    x += w + gap;
    return item;
  });
}

// 実際の動画解像度 (videoAspect = width/height) からレイアウト全体の
// aspect-ratio を求める。これによりセル内 letterbox がゼロになり、境界は
// CSS の gap だけになる (= 全レイアウト一律に揃う)。
// 純粋関数なので単体テスト対象。free / room は絶対配置で aspect 不要のため
// ここには含めない (呼び出し側で分岐)。
export function computeLayoutAspect(
  layout: string,
  videoAspect: number,
): number {
  switch (layout) {
    case "horizontal":
      return 2 * videoAspect;
    case "vertical":
      return videoAspect / 2;
    case "pip":
      return videoAspect;
    case "main-right":
      // main 2/3 幅 + sub 1/3 幅 × 2 縦並び。layout_aspect = video_aspect × 1.5
      return videoAspect * 1.5;
    case "main-bottom":
      // main 全幅 + sub 半幅 × 2 横並び。layout_aspect = video_aspect × 2/3
      return (videoAspect * 2) / 3;
    case "columns":
      return 3 * videoAspect;
    case "rows":
      return videoAspect / 3;
    default:
      return videoAspect; // 1 fullscreen
  }
}
